import { isEmpty } from 'lodash'
import PropTypes from 'prop-types'
import { PureComponent } from 'react'
import { connect } from 'react-redux'
import { fetchChannel } from 'shared/api/channels'
import ChannelInfo from 'shared/components/channel-info/info'
import Screensaver from 'shared/components/screensaver/screensaver'
import SharedVideoPaginationCarousel from 'shared/components/video-preview-thumbnail/pagination-carousel'
import gifProps from 'shared/prop-types/gif'
import * as selectors from 'shared/redux/reducers'
import { setGTADataLayer } from 'shared/util/analytics'
import { getUrl } from 'shared/util/gif-extract'
import { grid3, grid9 } from 'shared/util/grid'
import { WhenInView } from 'shared/util/in-view'
import { getSearchTitle } from 'shared/util/search'
import styled from 'styled-components'
import ChannelBanner from './channel-banner'
import ChannelPrivacy from './channel-privacy'
import ChannelChildren from './children/children'
import ChannelGrid from './gifs/grid'
import ChannelHeader from './header/header'
import ListChannel from './list/list-channel'
import Stories from './stories'
import { VideoPreviewChannelGrid } from 'shared/components/video-preview-grid'
const { arrayOf, bool, string, number, func } = PropTypes

const Container = styled.div`
    clear: both;
    padding-top: 30px;
`

const Sidebar = styled.div`
    float: left;
    margin-right: gridSpacing;
    width: ${grid3}px;
`

const Content = styled.div`
    float: left;
    width: ${grid9}px;
`

const VideoPaginationCarousel = styled(SharedVideoPaginationCarousel)`
    margin-top: 30px;
`

const EnhancedChannelHeader = connect((state, props) => {
    const { searchQuery: query, channel, isTagView, displayName: title } = props
    if (!query) {
        return {
            title,
        }
    }
    const gifCount = selectors.getGifCountByGroupId(state, props)
    const { content_type: contentType } = selectors.getChannel(state, channel.id)
    const gifCountTitle = getSearchTitle(gifCount, contentType, isTagView)
    return {
        title: gifCountTitle ? `${gifCountTitle}` : ``,
    }
})(ChannelHeader)

export default class RootChannel extends PureComponent {
    static propTypes = {
        channelId: number.isRequired,
        channelType: string,
        autoPlay: bool,
        total: string,
        query: string,
        isFetching: bool,
        gifs: arrayOf(gifProps),
        fetchChannel: func.isRequired,
        fetchGifs: func,
        screensaverUrl: bool,
        toggleAutoPlay: func.isRequired,
        showGridNavInHeader: bool.isRequired,
    }
    state = {
        channel: {},
    }
    async componentDidMount() {
        const { channelId, channelType } = this.props

        setGTADataLayer({ dimension: `ChannelPage`, value: channelId })
        // logs for pageview
        let channel
        if (channelType === 'video_channel') {
            channel = await fetchChannel(channelId, 'videos')
        } else {
            channel = await fetchChannel(channelId)
        }
        this.setState({ channel })
    }
    render() {
        const {
            autoPlay,
            gifs,
            query,
            fetchChannel,
            toggleAutoPlay,
            showGridNavInHeader,
            channelType,
            channelId,
            gridType,
            fetchGifs,
            isFetching,
            isOwner,
            groupId,
            editMode,
            toggleEditMode,
            updateEditMode,
        } = this.props
        const { channel } = this.state
        const {
            parent,
            description,
            display_name,
            screensaver_gif,
            has_children,
            user,
            is_live,
            children: channelChildren,
        } = channel

        const username = user ? user.username : ''
        const isChild = !!parent
        const isSearch = !isEmpty(query)
        const hasChannel = !isEmpty(channel)
        const hasGifs = !isEmpty(gifs)
        const screensaverUrl = getUrl(screensaver_gif, 'fixed_height') || ''
        const isTagView = channelType === 'tagged_channel'

        const isLeaf = isChild && !has_children
        const hasVideo = channel.video_channel && channel.video_channel.count > 0

        return (
            <Container>
                <ChannelPrivacy isOwner={isOwner} channel={channel} />
                <ChannelBanner channel={channel} />
                <Sidebar>
                    <ChannelInfo
                        channel={channel}
                        isOwner={isOwner}
                        searchQuery={query}
                        isTagView={isTagView}
                        isLoading={Boolean(!user)}
                        user={user}
                    />
                </Sidebar>
                <Content>
                    {hasChannel && (
                        <EnhancedChannelHeader
                            displayName={display_name || user.username}
                            searchQuery={query}
                            hasDescription={!!(isChild && description)}
                            autoPlay={autoPlay}
                            hideAutoplay={(!hasGifs && isEmpty(channelChildren)) || channel.content_type === 'video'}
                            onToggleAutoPlay={toggleAutoPlay}
                            channel={channel}
                            showGridNavigation={isLeaf ? false : showGridNavInHeader}
                            isChild={isChild}
                            isTagView={isTagView}
                            groupId={groupId}
                        />
                    )}
                    {username && !isChild && !isSearch && gridType !== 'list' && (
                        <Stories isOwner={isOwner} username={username} title={'Stories'} />
                    )}
                    {hasChannel && gridType === 'list' ? (
                        <ListChannel gridTemplate={'channel'} {...this.props} id={channelId} isLive={is_live} />
                    ) : (
                        <div>
                            {hasVideo && (
                                <VideoPaginationCarousel
                                    title={`Videos`}
                                    channel={channel.video_channel}
                                    titleUrl={channel.video_channel.url}
                                    width={grid9}
                                    hideAttribution
                                />
                            )}
                            {!isSearch && !isTagView && (
                                <ChannelChildren
                                    channelChildren={channelChildren}
                                    isBranch={isChild && has_children}
                                    fetchChannel={fetchChannel}
                                    autoPlay={autoPlay}
                                />
                            )}
                            {hasChannel && (
                                <WhenInView dontUnmount>
                                    {channelType === 'video_channel' ? (
                                        <VideoPreviewChannelGrid
                                            channelId={channel.id}
                                            previewWidth={grid3}
                                            hideAttribution
                                        />
                                    ) : (
                                        <ChannelGrid
                                            {...this.props}
                                            channel={channel}
                                            gridTemplate={'channel'}
                                            searchQuery={query}
                                            channelId={channelId}
                                            showNavigation={true}
                                            isChild={isChild}
                                            isSearch={isSearch}
                                            gridProps={{
                                                autoPlay,
                                                isFetching,
                                                groupId,
                                                toggleEditMode,
                                                updateEditMode,
                                                editMode,
                                                gifs,
                                                fetchGifs,
                                            }}
                                            search={query}
                                            isOwner={isOwner}
                                            user={user}
                                        />
                                    )}
                                </WhenInView>
                            )}
                        </div>
                    )}
                </Content>
                {screensaverUrl ? <Screensaver images={[screensaverUrl]} /> : null}
            </Container>
        )
    }
}
